import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormSchemaCreate } from './type';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { QuestionMarkCircledIcon } from '@radix-ui/react-icons';
import { Tip } from '@/components/ui/tooltip';
import React, { useEffect } from 'react';
import { useGetSpecAndPricingInstanceGPU } from '@/service/one-click-deploy/hook';
import {
  SpecAndPricingInstanceGPUWithDiscount,
  SpecAndPricingInstanceGPU,
  OptimizationTechniqueLabel,
  OptimizationTechnique,
} from '@/service/one-click-deploy/type';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

import { Separator } from '@/components/ui/separator';
import DisplayOptimized from './DisplayOptimized';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CircleAlert, ServerOff } from 'lucide-react';
import { mapOptionProvider } from '../../../../constants/provider';
import { fNameRegion } from '@/utils/formatLabel';
import { orderBy } from 'lodash';

const FormSpec = () => {
  const form = useFormContext<FormSchemaCreate>();
  const { instance, model } = useWatch<FormSchemaCreate>();

  const specAndPricingInstanceGPU = useGetSpecAndPricingInstanceGPU();
  const isLoadingInstance = specAndPricingInstanceGPU.isLoading;

  // const optionsOptimizationTechnique = Object.entries(
  //   OptimizationTechniqueLabel
  // ).map(([value, label]) => ({
  //   value,
  //   label,
  //   disabled:
  //     value === OptimizationTechnique.No
  //       ? false
  //       : model?.architecture !== 'Attention',
  // }));
  const optionsOptimizationTechnique = [
    {
      value: OptimizationTechnique.Basic,
      label: OptimizationTechniqueLabel[OptimizationTechnique.Basic],
      disabled: false,
    },
  ];

  const optionProvider = Object.entries(mapOptionProvider)
    .filter(([key]) =>
      specAndPricingInstanceGPU.data?.some((item) => item.provider.toLowerCase() === key)
    )
    .map(([key, value]) => ({
      provider: key,
      ...value,
    }));

  const optionsRegion = getUniqueRegions(
    specAndPricingInstanceGPU?.data?.filter(
      (item) => item.provider.toLocaleLowerCase() === instance?.provider
    ) || []
  )?.map((region) => ({
    value: region,
    label: fNameRegion(region),
  }));

  const optionsGPU =
    (specAndPricingInstanceGPU?.data &&
      orderBy(
        processGPUPricing(
          specAndPricingInstanceGPU?.data?.filter(
            (item) =>
              item.provider.toLocaleLowerCase() === instance?.provider &&
              item.region === (instance?.region || optionsRegion[0]?.value)
          )
        ),
        'price',
        'asc'
      ).map((item) => ({
        label: item.gpu_name,
        ...item,
      }))) ||
    [];
  // console.log('optionsGPU', optionsGPU);

  const fCheckSupportVRAM = (vram: number) => {
    if (!model?.minVRamRequiredGB) return true;
    return vram >= model?.minVRamRequiredGB;
  };

  useEffect(() => {
    if (instance?.provider && optionsRegion.length > 0 && !instance?.region) {
      form.setValue('instance.region', optionsRegion[0]?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance?.provider, optionsRegion]);

  useEffect(() => {
    if (instance?.provider && instance?.region) {
      form.setValue(
        'instance.gpu',
        optionsGPU?.filter((option) => {
          const isUnSupportVRAM = !fCheckSupportVRAM(option.vram!);
          const isAvailable = option.is_available;
          const isDisabled = isUnSupportVRAM || !isAvailable;
          return !isDisabled;
        })[0]?.pricing_id,
        {
          shouldValidate: true,
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance?.provider, instance?.region]);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Instance Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          {/* GPU */}
          <div className="grid gap-4 sm:grid-cols-1">
            <FormField
              control={form.control}
              name="instance.provider"
              render={({ field }) => (
                <>
                  <RadioGroup
                    value={field.value}
                    onValueChange={(e) => {
                      field.onChange(e);
                      const option = getUniqueRegions(
                        specAndPricingInstanceGPU?.data?.filter(
                          (item) => item.provider === e
                        ) || []
                      );
                      form.setValue('instance.region', option[0]);
                    }}
                    className="grid grid-cols-3 gap-4"
                  >
                    {optionProvider.map((option) => (
                      <div key={option.provider}>
                        <RadioGroupItem
                          value={option.provider}
                          id={option.provider}
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor={option.provider}
                          className="flex cursor-pointer items-center gap-2 rounded-md border-2 border-muted bg-popover px-4 py-2 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-muted-foreground [&:has([data-state=checked])]:border-muted-foreground"
                        >
                          <option.icon className='inline dark:hidden h-6 w-6'/>
                          <option.iconDark className='hidden dark:inline h-6 w-6'/>
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </>
              )}
            />
            {/* region  */}
            <FormField
              control={form.control}
              name="instance.region"
              render={({ field }) => (
                <FormItem className="max-w-60">
                  {/* <FormLabel className="flex gap-2">Region</FormLabel> */}
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isLoadingInstance}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {optionsRegion.map((option) => (
                          <SelectItem key={option.label} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="instance.gpu"
              render={({ field, fieldState: { error } }) => (
                <>
                  <div className="flex items-center gap-2 overflow-hidden">
                    <div
                      className={cn(
                        'text-nowrap font-semibold text-muted-foreground',
                        error && 'text-destructive'
                      )}
                    >
                      Select Instance Type
                    </div>
                    <Separator className={cn(error && 'bg-destructive')} />
                  </div>
                  <FormItem>
                    <RadioGroup
                      value={field.value}
                      onValueChange={(e) => {
                        field.onChange(e);
                      }}
                      className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4"
                    >
                      {optionsGPU.map((option) => {
                        const isUnSupportVRAM = !fCheckSupportVRAM(
                          option.vram!
                        );
                        const isAvailable = option.is_available;
                        const isDisabled = isUnSupportVRAM || !isAvailable;
                        return (
                          <div key={option.pricing_id}>
                            <RadioGroupItem
                              key={option.pricing_id}
                              value={option.pricing_id}
                              id={option.pricing_id}
                              className="peer sr-only"
                              disabled={isDisabled}
                            />
                            <Label
                              htmlFor={option.pricing_id}
                              className={cn(
                                'flex cursor-pointer flex-col gap-2 rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-muted-foreground [&:has([data-state=checked])]:border-muted-foreground',
                                'peer-disabled:bg-foreground/5'
                              )}
                            >
                              <div className="flex justify-between gap-1">
                                <span className="font-semibold">
                                  {option.gpu_name}
                                </span>
                                <span className="">${option.price} /hr </span>
                              </div>

                              <span
                                className={cn(
                                  '-mt-2 text-end text-xs text-muted-foreground line-through',
                                  !option.isDiscounted && 'invisible'
                                )}
                              >
                                ${option.originalPrice} /hr
                              </span>
                              <div className="flex justify-between gap-1">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span
                                        className={cn(
                                          'flex items-center gap-1 text-xs text-muted-foreground',
                                          isUnSupportVRAM && 'text-destructive'
                                        )}
                                      >
                                        {option.vram} GB VRAM{' '}
                                        {isUnSupportVRAM && (
                                          <CircleAlert className="h-4 w-4" />
                                        )}
                                      </span>
                                    </TooltipTrigger>
                                    {isUnSupportVRAM && (
                                      <TooltipContent>
                                        <p>Insufficient vram</p>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </TooltipProvider>
                                {!isAvailable && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-center gap-1 text-xs text-destructive">
                                          <ServerOff className="h-3 w-3" />
                                          <span>Unavailable</span>
                                        </div>
                                      </TooltipTrigger>
                                      {/* <TooltipContent>
                                        <p>Unavailable</p>
                                      </TooltipContent> */}
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            </Label>
                          </div>
                        );
                      })}
                    </RadioGroup>
                  </FormItem>
                  <Separator className={cn(error && 'bg-destructive')} />
                </>
              )}
            />
            {/* optimizationTechnique  */}
            <FormField
              control={form.control}
              name="instance.optimizationTechnique"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex gap-2">
                    Optimization Technique
                    <Tip content="test">
                      <QuestionMarkCircledIcon />
                    </Tip>
                  </FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {optionsOptimizationTechnique.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                          >
                            {option.label} {option.disabled && '(Unavailable)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* <DisplayOptimized /> */}
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default FormSpec;

function processGPUPricing(
  data: SpecAndPricingInstanceGPUWithDiscount[]
): SpecAndPricingInstanceGPUWithDiscount[] {
  const groupedByGPUType = data.reduce<
    Record<string, SpecAndPricingInstanceGPUWithDiscount[]>
  >((acc, item) => {
    if (!acc[item.gpu_id]) {
      acc[item.gpu_id] = [];
    }
    acc[item.gpu_id].push(item);
    return acc;
  }, {});

  const result: SpecAndPricingInstanceGPUWithDiscount[] = [];

  for (const gpuType in groupedByGPUType) {
    const items = groupedByGPUType[gpuType];
    items.sort((a, b) => b.priority - a.priority);
    result.push(items[0]);
  }
  return result;
}

function getUniqueRegions(data: SpecAndPricingInstanceGPU[]): string[] {
  const uniqueRegions = new Set(data.map((item) => item.region));
  return Array.from(uniqueRegions);
}
